import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  studentSurfaceState,
  daysUntil,
  STUDENT_RENEWAL_WINDOW_DAYS,
} from "../../../utils/studentEligibility";

const STUDENT_CODE_HASH = "#student-code";

function readDismissed(key) {
  if (!key || typeof sessionStorage === "undefined") return false;
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

/**
 * Slim surface banner for student accounts:
 * - not verified yet → "your account is not activated yet"
 * - verified but expiring within STUDENT_RENEWAL_WINDOW_DAYS → "renew your student account"
 * The CTA opens Settings on the student code. Close hides the banner for this session.
 */
const StudentStatusBanner = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const state = studentSurfaceState(user, STUDENT_RENEWAL_WINDOW_DAYS);
  const visible = state === "notActivated" || state === "expiringSoon" || state === "expired";
  const storageKey =
    visible && (user?.id || user?._id)
      ? `studentStatusBannerDismissed:${user.id || user._id}:${state}`
      : "";
  const [closedFor, setClosedFor] = useState("");
  const dismissed = storageKey
    ? closedFor === storageKey || readDismissed(storageKey)
    : closedFor === "closed";

  if (!visible || dismissed) return null;

  const isExpiring = state === "expiringSoon";
  const isExpired = state === "expired";
  const days = Math.max(0, daysUntil(user?.studentValidUntil) ?? 0);

  const title = isExpired
    ? t("student.bannerExpiredTitle")
    : isExpiring
      ? t("student.bannerExpiringTitle")
      : t("student.bannerNotActivatedTitle");
  const body = isExpired
    ? t("student.bannerExpiredBody")
    : isExpiring
      ? t("student.bannerExpiringBody", { count: days })
      : t("student.bannerNotActivatedBody");

  const tone = isExpired
    ? "bg-orange-50 border-orange-200 text-orange-950"
    : isExpiring
      ? "bg-amber-50 border-amber-200 text-amber-950"
      : "bg-violet-50 border-violet-200 text-violet-950";
  const ctaTone = isExpired
    ? "text-orange-900 border-orange-300 hover:bg-orange-100"
    : isExpiring
      ? "text-amber-900 border-amber-300 hover:bg-amber-100"
      : "text-violet-900 border-violet-300 hover:bg-violet-100";

  const handleClose = () => {
    if (storageKey) {
      try {
        sessionStorage.setItem(storageKey, "1");
      } catch {
        /* ignore */
      }
    }
    setClosedFor(storageKey || "closed");
  };

  return (
    <div
      className={`relative shrink-0 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 px-3 py-2.5 pr-10 border-b text-xs sm:text-sm ${tone}`}
      role="status"
    >
      <span aria-hidden className="text-base leading-none">
        🎓
      </span>
      <span className="font-bold text-center sm:text-left">{title}</span>
      <span className="hidden sm:inline opacity-90">{body}</span>
      <Link
        to={{ pathname: "/settings", hash: STUDENT_CODE_HASH }}
        state={{ focusStudentCode: true }}
        className={`font-semibold underline-offset-2 rounded-full border px-2.5 py-0.5 transition-colors ${ctaTone}`}
      >
        {t("student.bannerCta")}
      </Link>
      <button
        type="button"
        onClick={handleClose}
        className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors ${ctaTone}`}
        aria-label={t("common.close")}
      >
        <FiX size={16} aria-hidden />
      </button>
    </div>
  );
};

export default StudentStatusBanner;
